import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// UI state for notifications
export interface NotificationUiState {
  // UI states for display
  isNotificationPanelOpen: boolean;
  selectedNotificationId: string | null;
  
  // Temporary UI states for optimistic updates
  optimisticReadIds: string[]; // IDs marked as read optimistically
  
  // Error states for UI display
  lastError: string | null;
}

const initialState: NotificationUiState = {
  isNotificationPanelOpen: false,
  selectedNotificationId: null,
  optimisticReadIds: [],
  lastError: null
};

// Note: Notification data is now fetched using RTK Query hooks from the API layer
// See: useGetNotificationFeedQuery from '@/api/features/users/endpoints/notifications'
// See: useGetNotificationUnreadCountQuery from '@/api/features/users/endpoints/notifications'
// See: useGetNotificationSettingsQuery from '@/api/features/users/endpoints/notifications'

export const notificationUiSlice = createSlice({
  name: 'notificationUi',
  initialState,
  reducers: {
    toggleNotificationPanel: (state) => {
      state.isNotificationPanelOpen = !state.isNotificationPanelOpen;
    },
    setNotificationPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isNotificationPanelOpen = action.payload;
    },
    selectNotification: (state, action: PayloadAction<string | null>) => {
      state.selectedNotificationId = action.payload;
    },
    // For optimistic updates when marking as read
    addOptimisticRead: (state, action: PayloadAction<string>) => {
      if (!state.optimisticReadIds.includes(action.payload)) {
        state.optimisticReadIds.push(action.payload);
      }
    },
    removeOptimisticRead: (state, action: PayloadAction<string>) => {
      state.optimisticReadIds = state.optimisticReadIds.filter(id => id !== action.payload);
    },
    clearOptimisticReads: (state) => {
      state.optimisticReadIds = [];
    },
    setLastError: (state, action: PayloadAction<string | null>) => {
      state.lastError = action.payload;
    },
  }
});

export const {
  toggleNotificationPanel,
  setNotificationPanelOpen,
  selectNotification,
  addOptimisticRead,
  removeOptimisticRead,
  clearOptimisticReads,
  setLastError
} = notificationUiSlice.actions;

// Selectors — typed with slice state to avoid circular dep on RootState
type WithNotifications = { notifications: NotificationUiState };
export const selectIsNotificationPanelOpen = (state: WithNotifications) => state.notifications.isNotificationPanelOpen;
export const selectSelectedNotificationId = (state: WithNotifications) => state.notifications.selectedNotificationId;
export const selectOptimisticReadIds = (state: WithNotifications) => state.notifications.optimisticReadIds;
export const selectLastError = (state: WithNotifications) => state.notifications.lastError;

export default notificationUiSlice.reducer;